import { FC, Fragment, useMemo } from 'react';

import { Text } from '@hh.ru/magritte-ui';

import { getTextStyle } from 'src/components/ResumeCard/utils/getTextStyle';
import { useSelector } from 'src/hooks/useSelector';
import { Permission } from 'src/models/resume/resumeCommon.types';
import type { ResumeCardData } from 'src/models/resumeCard';

import Age from 'src/components/ResumeCard/Header/ApplicantInfoList/Age';
import FullName from 'src/components/ResumeCard/Header/ApplicantInfoList/FullName';
import PfpStub from 'src/components/ResumeCard/Header/ApplicantInfoList/PfpStub';
import Range from 'src/components/ResumeCard/Header/ApplicantInfoList/Range';

interface HookProps {
    isNameHiddenByApplicant: boolean;
    fullName: string | null;
    resume: ResumeCardData;
    vacancyId?: number;
    isPfpVacancy: boolean | null;
}

type UseApplicantInfoListHook = (props: HookProps) => Array<JSX.Element>;

interface IsPfpMaskedHookProps {
    permission: Permission;
    isPfpVacancy: boolean | null;
    hasPfpTopics: boolean | undefined;
}

type UseIsPfpMaskedHook = (props: IsPfpMaskedHookProps) => boolean;

export const useIsPfpMasked: UseIsPfpMaskedHook = ({ permission, isPfpVacancy, hasPfpTopics }) => {
    return permission === Permission.ViewWithoutContacts ? isPfpVacancy || !!hasPfpTopics : false;
};
export const useApplicantInfoList: UseApplicantInfoListHook = ({
    isNameHiddenByApplicant,
    fullName,
    resume,
    vacancyId,
    isPfpVacancy,
}) => {
    const topicId = useSelector((state) => state.router.location.query.t);
    const {
        hasPfpTopics,
        _attributes: { permission, id },
    } = resume;

    const isPfpMasked = useIsPfpMasked({ permission, isPfpVacancy, hasPfpTopics });

    const range = useSelector((state) => state.candidatesList?.filterDistance);

    return useMemo(() => {
        const accList = [];

        const age = resume.age?.[0]?.string;
        const ageComp = age ? <Age age={age} key="age" /> : null;
        if (isPfpMasked) {
            accList.push(<PfpStub resumeId={id} vacancyId={vacancyId} topicId={resume.topicId || topicId} />);
            if (ageComp) {
                accList.push(ageComp);
            }
            return accList;
        }

        if (fullName || isNameHiddenByApplicant) {
            accList.push(
                <FullName fullName={fullName} isHiddenByApplicant={isNameHiddenByApplicant} key="full-name" />
            );
        }

        if (ageComp) {
            accList.push(ageComp);
        }

        if (range) {
            accList.push(<Range range={range} key="range" />);
        }

        return accList;
    }, [fullName, id, isNameHiddenByApplicant, isPfpMasked, range, resume.age, resume.topicId, topicId, vacancyId]);
};

interface Props {
    isVisited: boolean;
    list: Array<JSX.Element>;
    isPfpMasked: boolean;
}

const ApplicantInfoList: FC<Props> = ({ isVisited, list, isPfpMasked }) => {
    if (!list.length) {
        return null;
    }

    return (
        <Text Element="span" typography="label-3-regular" style={getTextStyle(isVisited)}>
            {list.map((item, index) => (
                <Fragment key={index}>
                    {index !== 0 && !isPfpMasked && <span>{', '}</span>}
                    {item}
                </Fragment>
            ))}
        </Text>
    );
};

export default ApplicantInfoList;
